class Score {
  constructor(
    readonly match: number,
    readonly mismatch: number,
    readonly gap: number,
    readonly bonus: number,
  ) {}

  compare(b: Score) {
    if (this.match < b.match) return -1;
    if (this.match > b.match) return 1;
    if (this.bonus < b.bonus) return -1;
    if (this.bonus > b.bonus) return 1;
    if (this.mismatch > b.mismatch) return -1;
    if (this.mismatch < b.mismatch) return 1;
    if (this.gap > b.gap) return -1;
    if (this.gap < b.gap) return 1;
    return 0;
  }
}

export const fuzzyScore = (expression: string, candidate: string = ''): Score => {
  let expressionIndex = 0;
  let candidateIndex = 0;
  let match = 0;
  let misMatch = 0;
  let gap = 0;
  let streak = 0;
  let streakBonus = 0;
  let prefixBonus = 0;

  while (expressionIndex < expression.length && candidateIndex < candidate.length) {
    const charA = expression[expressionIndex];
    const charB = candidate[candidateIndex];

    if (charA === charB) {
      match += 1;
      expressionIndex += 1;
      streak += 1;
      if (streak > 1) {
        streakBonus = Math.max(streakBonus, streak - 1);
      }
    } else {
      misMatch += 1;
      gap += 1;
      streak = 0;
    }
    candidateIndex += 1;
  }
  misMatch += expression.length - expressionIndex;
  misMatch += candidate.length - candidateIndex;

  if (candidate.startsWith(expression)) {
    prefixBonus = expression.length;
  }

  return new Score(match, misMatch, gap, streakBonus + prefixBonus);
};

export const instrumentFuzzySearch = (
  keyword: string,
  symbols: string[],
  instrumentBySymbol?: Record<
    string,
    {
      symbol: string;
      displayName: string;
      displayShortName: string;
      keywords?: string[];
      isSpot: boolean;
      positionUnit?: string;
      baseCurrency?: string;
    }
  >,
) => {
  if (!keyword.trim() || !instrumentBySymbol) return symbols;

  const expression = keyword.toLowerCase();
  return symbols
    .map((symbol) => instrumentBySymbol[symbol])
    .filter((x): x is NonNullable<typeof x> => !!x)
    .map(({ displayShortName, displayName, keywords = [], symbol, isSpot, positionUnit, baseCurrency }) => {
      const ticker = isSpot ? baseCurrency : positionUnit;
      const targets = [displayShortName, displayName, ...(ticker ? [ticker] : []), ...keywords];
      const score = targets
        .map((x) => fuzzyScore(expression, x.toLowerCase()))
        .sort((a, b) => b.compare(a))
        .at(0)!;
      return { symbol, score, isSpot, displayShortName };
    })
    .filter((item) => item.score.match > 0)
    .sort((a, b) => {
      const scoreCompare = b.score.compare(a.score);
      if (scoreCompare !== 0) return scoreCompare;
      return a.displayShortName.localeCompare(b.displayShortName);
    })
    .sort((a, b) => {
      if (a.displayShortName !== b.displayShortName) return 0;
      if (a.isSpot === b.isSpot) return 0;
      return a.isSpot ? 1 : -1;
    })
    .map((item) => item.symbol);
};
